import { useCallback, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Button, Text, tokens, useNotify } from '@hotjar/hotsauce';

import { smartPanelElementFullWidthStyles } from '../../../../utils/layout';

type SessionErrorsSegmentEmptyStateParams = {
  hasSessionSettingsUpdatePermission: boolean;
  siteId: number;
};

const Description = styled(Text)`
  padding: ${tokens.spacer.x1} 0;
`;

const EnableErrorCapturingButton = styled(Button)`
  margin: 0;
  ${smartPanelElementFullWidthStyles()};

  &&:focus {
    box-shadow: unset;
    border-color: ${tokens.color.ui_border_active};
  }
`;

export const SessionErrorsSegmentEmptyState = ({
  hasSessionSettingsUpdatePermission,
  siteId,
}: SessionErrorsSegmentEmptyStateParams): JSX.Element => {
  const { t } = useTranslation('heatmaps');
  const { showToast } = useNotify();
  const [userHasEnabledCapturing, setUserHasEnabledCapturing] = useState(false);

  // Logic duplicated from 'packages/legacy/src//webapp/features/Settings/api'
  // TODO: Extract into a common re-usable place out of legacy.
  const onSubmit = useCallback(async () => {
    try {
      await axios.post(`/api/v1/sites/${siteId}/settings/sessions`, {
        session_capture_console_consent: true,
        action: 'update_session_settings',
      });
      showToast({
        kind: 'success',
        text: t('smart-panel.session-errors.empty-state.console-tracking-enabled-success'),
      });
      setUserHasEnabledCapturing(true);
    } catch {
      showToast({
        kind: 'danger',
        text: t('smart-panel.session-errors.empty-state.console-tracking-enabled-error'),
      });
    }
  }, [siteId, showToast, t]);

  if (userHasEnabledCapturing) {
    return (
      <Description as="span" textType="caption" textColor="text_light">
        {t('smart-panel.session-errors.empty-state.console-tracking-enabled')}
      </Description>
    );
  }

  return (
    <>
      <Description as="span" textType="caption" textColor="text_light">
        {t('smart-panel.session-errors.empty-state.description')}
      </Description>
      {hasSessionSettingsUpdatePermission ? (
        <EnableErrorCapturingButton kind="ghost" onClick={onSubmit}>
          {t('smart-panel.session-errors.empty-state.button')}
        </EnableErrorCapturingButton>
      ) : (
        <Text as="span" textType="caption" textColor="text_light">
          {t('smart-panel.session-errors.empty-state.admin-info')}
        </Text>
      )}
    </>
  );
};
